import { useMemo, type ReactNode } from 'react';
import { getMeta } from '../api/endpoints';
import type { DateFormats } from '../lib/dateFormat';
import { buildTenantTime, TenantTimeContext } from '../lib/tenantTime';
import { isValidZone } from '../lib/timeZone';
import { useLoad } from '../lib/useLoad';

/** What one GET /meta contributes here: the zone, and the two display patterns. */
interface TenantClock {
  zone: string | null;
  formats: Partial<DateFormats> | null;
}

/**
 * Loads the tenant's dispatch timezone AND its date/time patterns from GET
 * /meta, and binds every timestamp formatter to them (see lib/tenantTime).
 * Children render immediately on the defaults and re-render once it lands —
 * blocking the whole shell on one lookup would cost more than the sub-second
 * convergence it buys.
 *
 * `zone`/`formats` short-circuit the fetch (fixtures, previews, anything
 * without a session). A failed load is not an error state: no zone means the
 * viewer's own clock, which is what the console did before this existed, and no
 * patterns means dispatch's own defaults.
 */
export function TenantTimeProvider({
  children,
  zone: fixedZone,
  formats: fixedFormats,
}: {
  children: ReactNode;
  zone?: string | null;
  formats?: Partial<DateFormats> | null;
}) {
  const load = useLoad<TenantClock>(
    () =>
      fixedZone !== undefined
        ? Promise.resolve({ zone: fixedZone, formats: fixedFormats ?? null })
        : getMeta()
            .then((meta) => ({
              zone: meta.timezone,
              formats: { dateFormat: meta.dateFormat, timeFormat: meta.timeFormat },
            }))
            .catch(() => ({ zone: null, formats: null })),
    [fixedZone, fixedFormats],
  );

  const value = useMemo(
    () =>
      buildTenantTime(isValidZone(load.data?.zone) ? load.data.zone : null, load.data?.formats),
    [load.data],
  );

  return <TenantTimeContext.Provider value={value}>{children}</TenantTimeContext.Provider>;
}
